/**
 * Bugsnag 
 */
import React from 'react'
import Bugsnag from '@bugsnag/js'
import BugsnagPluginReact from '@bugsnag/plugin-react'

const ERROR_BOUNDARY_META_KEY = 'Error Boundary'

/**
 * Error messages that are pure noise and are never reported: third-party
 * script hiccups and Next.js navigation invariants nothing can act on.
 *
 * Deliberately NOT here any more (5.51.3): 'Minified React error' — that is
 * every React render crash in a production build, i.e. exactly what the Page
 * error boundary exists to surface — and 'Failed to fetch', Chromium's wording
 * of a failed fetch. Dropping the latter while keeping WebKit's 'Load failed'
 * made a browser-agnostic network failure look Safari-only for a week.
 */
const discardedErrors = [
  'Failed to load Stripe.js',
  'Request aborted',
  'RB.jsonPIntegrations',
  'Invariant: attempted to hard navigate to the same URL',
  'RB.jsonPUID',
]

/**
 * Messages that mean "the network request did not complete" in the various
 * browsers' words. Reported, but as a warning when the app recovered on its
 * own; a network failure that took the page down (unhandled) stays an error.
 */
const networkErrorMessages = [
  'Failed to fetch',
  'Load failed',
  'Network Error',
  'Network error',
  'NetworkError',
  'Failed to load resource',
]

const messageMatches = (event, needles) =>
  Boolean(event?.errors?.find(error => needles.some(needle => error.errorMessage?.includes?.(needle))))

/**
 * bugsnagOnError: the global Bugsnag onError callback. Exported for tests.
 *
 * @param {object} event the Bugsnag event
 * @returns {boolean|undefined} false to discard the event
 */
export const bugsnagOnError = event => {
  if (!(event && event.errors)) {
    return false
  }
  if (messageMatches(event, discardedErrors)) {
    return false
  }
  if (messageMatches(event, networkErrorMessages)) {
    event.addMetadata('network', { failedRequest: true, unhandled: Boolean(event.unhandled) })
    if (!event.unhandled) {
      event.severity = 'warning'
    }
  }
  event.addMetadata(ERROR_BOUNDARY_META_KEY, {
    name: 'None: Global Error Handler'
  })
  return undefined
}

let initialized = false
const initialize = () => {
  if (typeof window === 'undefined') {
    return
  }  
  if (initialized) {
    return
  }
  initialized = true

  const BUGSNAG_API_KEY = process.env.NEXT_PUBLIC_BUGSNAG_API_KEY

  // determine bugsnag stage based on URL
  const releaseStage = (() => {
    const host = window.location.host
    // localhost: development
    if (/^localhost(:\d+)?$/.test(host)) {
      return 'development'
    }

    // www-dev: staging
    if (/-dev\./.test(host)) {
      return 'staging'
    }

    // otherwise it's production
    return 'production'
  })()

  Bugsnag.start({
    apiKey: BUGSNAG_API_KEY,
    appVersion: process.env.NEXT_PUBLIC_VERSION,
    plugins: [new BugsnagPluginReact()],
    enabledReleaseStages: [
      'production',
      'staging',
      // 'development'
    ],
    releaseStage: releaseStage,
    onError: bugsnagOnError,
  })
}

/**
 * notifyBugsnag: reports a handled error to Bugsnag with optional metadata.
 *
 * For errors we catch and recover from, so they don't stay invisible. Errors swallowed
 * into component state (see the availability search) never reach the global handler or
 * an error boundary, so without an explicit notify they are never reported at all.
 *
 * Safe to call from anywhere: no-ops on the server, initializes Bugsnag on demand, and
 * never throws — reporting an error must not break the flow that recovered from it.
 *
 * @param {Error} error the error to report
 * @param {object} [metadata] extra context, added under the "Details" tab
 * @param {string} [metadataKey='Details'] Bugsnag metadata section name
 * @param {'error'|'warning'|'info'} [severity] overrides Bugsnag's default
 *   'warning' for handled errors, e.g. 'error' for a defect that blocked a booking
 */
export const notifyBugsnag = (error, metadata, metadataKey = 'Details', severity) => {
  try {
    if (typeof window === 'undefined') {
      return
    }

    initialize()

    Bugsnag.notify(error, event => {
      if (severity) {
        event.severity = severity
      }
      if (metadata) {
        event.addMetadata(metadataKey, metadata)
      }
    })
  } catch (notifyError) {
    console.warn('notifyBugsnag:', notifyError)
  }
}

/**
 * setBugsnagUser: identifies user to Bugsnag
 *
 * @param {*} user
 */
export const setBugsnagUser = (user) => {
  initialize()
  Bugsnag.setUser(user.id, user.email, user.fullName)
}

/**
 * createBugsnagErrorBoundary: the error boundary to wrap the app in
 */
export const createBugsnagErrorBoundary = (boundaryName = 'Untitled Error Boundary') => ({ FallbackComponent, children }) => {
  initialize()

  if (typeof window === 'undefined') {
    return children
  }

  const ErrorBoundary = Bugsnag.getPlugin('react').createErrorBoundary(React)

  // add boundary name to error
  const onError = (event) => {
    event.clearMetadata(ERROR_BOUNDARY_META_KEY)
    event.addMetadata(ERROR_BOUNDARY_META_KEY, {
      name: boundaryName
    })

    event.errors.forEach((error) => {
      error.errorMessage = `${boundaryName}: ${error.errorMessage}`
    })
  }

  return (
    <ErrorBoundary FallbackComponent={FallbackComponent} onError={onError}>
      {children}
    </ErrorBoundary>
  )
}
